import { useMemo, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, LoadingView, SBCard } from "@/components/ui";
import { locationText, type LeagueCompetitionEventDisplay } from "@/models/eventDisplay";
import { actionAuditScopes } from "@/models/featureAccessPolicy";
import { activeLeagueManagementMembership, canShowHomeDashboardCard, preferredName } from "@/models/user";
import { useLeagueSnapshot } from "./useLeagueSnapshot";
import { relativeEventDayLabel, selectHomeCompetitionRows } from "./homeCompetitionOrdering";
import { useAppEnvironmentReport } from "@/features/ai/AppEnvironmentScreen";
import type {
  SBotAppEnvironmentControl,
  SBotAppEnvironmentSection,
  SBotAppEnvironmentSurfaceReport,
} from "@/features/ai/services/appEnvironmentContext";
import "./home.css";

// Port of Views/Tabs/HomeView.swift — greeting + league snapshot, laid out
// "race day first": next-up hero, schedule, tools rail, organizations, live & broadcast.

export function CompetitionName({ name }: { name: string }) {
  return <strong title={name}>{name}</strong>;
}

export function EventRow({ display }: { display: LeagueCompetitionEventDisplay }) {
  const location = locationText(display);
  const startsAt = display.startsAt;
  return (
    <Link to={`/competitions/${display.instance.id}`} className="event-row">
      <div className="event-date" aria-hidden="true">
        {startsAt ? (
          <>
            <span className="event-date-month">{startsAt.toLocaleString(undefined, { month: "short" })}</span>
            <span className="event-date-day">{startsAt.getDate()}</span>
          </>
        ) : (
          <span className="event-date-tbd">TBD</span>
        )}
      </div>
      <div className="event-row-main">
        <CompetitionName name={display.eventName} />
        <span>
          {startsAt
            ? startsAt.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })
            : "Unscheduled"}
          {location ? ` · ${location}` : ""}
        </span>
      </div>
      <div className="event-row-badges">
        {display.isGPSRace && <span className="badge badge-gps">GPS</span>}
        <span className={`badge badge-${display.instance.status}`}>{display.instance.status}</span>
      </div>
    </Link>
  );
}

type GlyphKind = "gps" | "overlay" | "data" | "org" | "activity" | "encoder";

function Glyph({ kind }: { kind: GlyphKind }) {
  switch (kind) {
    case "gps":
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="8" cy="8" r="2.6" />
          <path d="M8 1v2.4M8 12.6V15M1 8h2.4M12.6 8H15" strokeLinecap="round" />
        </svg>
      );
    case "overlay":
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
          <path d="M8 1.8 14.5 5 8 8.2 1.5 5Z" />
          <path d="m2.6 8.4 5.4 2.7 5.4-2.7M2.6 11.4 8 14.1l5.4-2.7" strokeLinecap="round" />
        </svg>
      );
    case "data":
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <ellipse cx="8" cy="3.6" rx="5.4" ry="2.1" />
          <path d="M2.6 3.6v8.8c0 1.16 2.42 2.1 5.4 2.1s5.4-.94 5.4-2.1V3.6M2.6 8c0 1.16 2.42 2.1 5.4 2.1s5.4-.94 5.4-2.1" />
        </svg>
      );
    case "org":
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M1.5 14.5h13M3 14.5V5.2L8 1.8l5 3.4v9.3M6.2 14.5v-3.4h3.6v3.4" strokeLinejoin="round" />
        </svg>
      );
    case "activity":
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M3.2 4.2A6 6 0 1 1 2 8" />
          <path d="M3.2 1.7v2.5H.7M8 4.7v3.6l2.3 1.4" strokeLinejoin="round" />
        </svg>
      );
    case "encoder":
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2.5 13.5c3-1 3.5-4.5 6-6s4.5-.5 5-4" />
          <circle cx="2.5" cy="13.5" r="1.3" />
          <circle cx="13.5" cy="3.5" r="1.3" />
        </svg>
      );
  }
}

function RowIcon({ kind }: { kind: GlyphKind }) {
  return (
    <span className={`event-icon event-icon-${kind}`} aria-hidden="true">
      <Glyph kind={kind} />
    </span>
  );
}

interface HomeTool {
  id: string;
  label: string;
  subtitle: string;
  to: string;
  icon: GlyphKind;
}

interface HomeGlanceItem {
  id: string;
  label: string;
  count: number;
  to: string;
}

function enterStyle(index: number): CSSProperties {
  return { "--home-enter": index } as CSSProperties;
}

export function HomeDashboard() {
  const { currentUser, isSBotLabsAdmin } = useAuth();
  const snapshot = useLeagueSnapshot(true);

  const greeting = currentUser ? `Welcome back, ${preferredName(currentUser).split(" ")[0]}` : "Welcome";
  // `relativeTo` is fixed per snapshot update, so the next-up label ticks over
  // at midnight only when the competition list changes (same as the ordering).
  const competitionRows = useMemo(
    () => selectHomeCompetitionRows(snapshot.competitions),
    [snapshot.competitions],
  );
  const { nextUp, schedule, gpsAndBroadcastOverlays: gpsRaces } = competitionRows;
  const athletes = snapshot.memberSummaries.filter((m) => m.roles.includes("athlete"));
  const staff = snapshot.memberSummaries.filter((m) => m.roles.some((role) => role !== "athlete"));
  const showSchedule = canShowHomeDashboardCard("leagueSchedule", currentUser, isSBotLabsAdmin);
  const showGPS = canShowHomeDashboardCard("gpsTracking", currentUser, isSBotLabsAdmin);
  const showCourseEncoder =
    isSBotLabsAdmin || (currentUser ? activeLeagueManagementMembership(currentUser) !== undefined : false);
  const showOrganizations = canShowHomeDashboardCard("organizations", currentUser, isSBotLabsAdmin);
  const showAthletes = canShowHomeDashboardCard("athletes", currentUser, isSBotLabsAdmin);
  const showLeagueStaff = canShowHomeDashboardCard("leagueStaff", currentUser, isSBotLabsAdmin);
  const showBroadcastOverlays = canShowHomeDashboardCard("broadcastOverlays", currentUser, isSBotLabsAdmin);
  const showExternalSources = canShowHomeDashboardCard("externalSources", currentUser, isSBotLabsAdmin);
  const showActivity = actionAuditScopes(currentUser, isSBotLabsAdmin, snapshot.leagueID).length > 0;
  const showLiveAndBroadcast = showGPS || showBroadcastOverlays;
  const isInitialLoading = snapshot.loading && snapshot.competitions.length === 0;
  const competitionCount = snapshot.competitions.length;
  const organizationCount = snapshot.organizations.length;
  const leadGPSRace = gpsRaces[0];

  const glanceItems = useMemo<HomeGlanceItem[]>(
    () => [
      ...(showSchedule ? [{ id: "competitions", label: "Competitions", count: competitionCount, to: "/home/schedule" }] : []),
      ...(showAthletes ? [{ id: "athletes", label: "Athletes", count: athletes.length, to: "/home/athletes" }] : []),
      ...(showLeagueStaff ? [{ id: "staff", label: "League staff", count: staff.length, to: "/home/staff" }] : []),
    ],
    [athletes.length, competitionCount, showAthletes, showLeagueStaff, showSchedule, staff.length],
  );

  // Tools are navigation, not content: one compact rail instead of a card each.
  // Broadcast Overlays has no league-level index route, so its tile opens the
  // lead GPS race's overlay list and disappears when there is no GPS race.
  const tools = useMemo<HomeTool[]>(
    () => [
      ...(showGPS ? [{ id: "gps", label: "GPS", subtitle: "Live map · history · data", to: "/home/gps", icon: "gps" as const }] : []),
      ...(showBroadcastOverlays && leadGPSRace
        ? [{
            id: "overlays",
            label: "Broadcast Overlays",
            subtitle: "Design and control",
            to: `/competitions/${leadGPSRace.instance.id}/overlays`,
            icon: "overlay" as const,
          }]
        : []),
      ...(showExternalSources
        ? [{ id: "external-data", label: "External Data", subtitle: "Datasets and imports", to: "/league/external-sources", icon: "data" as const }]
        : []),
      ...(showCourseEncoder
        ? [{ id: "course-encoder", label: "Course Encoder", subtitle: "KML · KMZ · GPX routes", to: "/league/course-encoder", icon: "encoder" as const }]
        : []),
      ...(showActivity
        ? [{ id: "activity", label: "Recent Activity", subtitle: "Last 24 hours · undo", to: "/home/activity", icon: "activity" as const }]
        : []),
    ],
    [leadGPSRace, showActivity, showBroadcastOverlays, showCourseEncoder, showExternalSources, showGPS],
  );

  const nextUpDisplay = nextUp?.display;
  const nextUpStartsAt = nextUpDisplay?.startsAt;
  const nextUpWhen = nextUp ? relativeEventDayLabel(nextUp.dayOffset) : undefined;
  const nextUpLocation = nextUpDisplay ? locationText(nextUpDisplay) : undefined;
  const nextUpDateText = nextUpStartsAt
    ? nextUpStartsAt.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "Unscheduled";

  const homeEnvironmentReport = useMemo<SBotAppEnvironmentSurfaceReport>(() => {
    const sections: SBotAppEnvironmentSection[] = [{
      id: "home-header",
      title: "Header",
      controls: [
        {
          id: "active-workspace",
          role: "status",
          label: currentUser?.activeContext?.displayName ?? "No active workspace",
          subtitle: "Active workspace",
          isSelected: true,
        },
        {
          id: "home-greeting",
          role: "status",
          label: greeting,
        },
      ],
    }];
    if (snapshot.error) {
      sections.push({
        id: "home-status",
        title: "Status",
        controls: [{ role: "status", label: "Home unavailable", value: snapshot.error }],
      });
    }
    if (showSchedule) {
      const nextUpControls: SBotAppEnvironmentControl[] = nextUpDisplay
        ? [
            {
              id: "next-up-competition",
              role: "link",
              label: nextUpDisplay.eventName,
              subtitle: [nextUpWhen, nextUpDateText, nextUpLocation].filter(Boolean).join(" · "),
              isEnabled: true,
            },
            { id: "next-up-open", role: "link", label: "Open competition", isEnabled: true },
            ...(nextUpDisplay.isGPSRace && showGPS
              ? [{ id: "next-up-map", role: "link" as const, label: "Live map", isEnabled: true }]
              : []),
            ...(nextUpDisplay.isGPSRace && showBroadcastOverlays
              ? [{ id: "next-up-overlays", role: "link" as const, label: "Overlays", isEnabled: true }]
              : []),
          ]
        : [{ id: "next-up-empty", role: "status", label: "No upcoming competitions" }];
      sections.push({ id: "next-up", title: "Next up", controls: nextUpControls });
      sections.push({
        id: "schedule",
        title: "Schedule",
        controls: [
          ...schedule.map<SBotAppEnvironmentControl>((display) => ({
            role: "link",
            label: display.eventName,
            subtitle: display.startsAt?.toLocaleString(),
            isEnabled: true,
          })),
          { id: "schedule-all", role: "link", label: `All (${competitionCount})`, isEnabled: true },
        ],
      });
    }
    if (tools.length > 0) {
      sections.push({
        id: "tools",
        title: "Tools",
        controls: tools.map<SBotAppEnvironmentControl>((tool) => ({
          id: `tool-${tool.id}`,
          role: "link",
          label: tool.label,
          subtitle: tool.subtitle,
          isEnabled: true,
        })),
      });
    }
    if (showLiveAndBroadcast) {
      sections.push({
        id: "live-broadcast",
        title: "Live & broadcast",
        controls: gpsRaces.length > 0
          ? gpsRaces.map<SBotAppEnvironmentControl>((display) => ({
              role: "link",
              label: display.eventName,
              subtitle: [showGPS ? "Map" : undefined, showBroadcastOverlays ? "Overlays" : undefined].filter(Boolean).join(" · "),
              isEnabled: true,
            }))
          : [{ id: "live-broadcast-empty", role: "status", label: "No GPS-enabled competitions" }],
      });
    }
    const stats: SBotAppEnvironmentControl[] = [
      ...glanceItems.map<SBotAppEnvironmentControl>((item) => ({
        id: `glance-${item.id}`,
        role: "value",
        label: item.label,
        value: `${item.count}`,
      })),
      ...(showOrganizations ? [{ id: "glance-organizations", role: "value" as const, label: "Organizations", value: `${organizationCount}` }] : []),
    ];
    if (stats.length > 0) sections.push({ id: "at-a-glance", title: "At a Glance", controls: stats });

    return {
      identity: "home",
      owningTab: "home",
      breadcrumbs: ["Home"],
      destinationType: "home",
      surfaceLevel: "tabRoot",
      surfaceDepth: 1,
      surfacePresentation: "tabRoot",
      screen: {
        title: "Home",
        state: !snapshot.leagueID
          ? "no eligible workspace dashboard"
          : snapshot.error
            ? "error"
            : snapshot.loading
              ? "loading"
              : "loaded",
        contextCompleteness: "semantic",
        sections,
      },
    };
  }, [
    competitionCount,
    currentUser?.activeContext?.displayName,
    glanceItems,
    gpsRaces,
    greeting,
    nextUpDateText,
    nextUpDisplay,
    nextUpLocation,
    nextUpWhen,
    organizationCount,
    schedule,
    showBroadcastOverlays,
    showGPS,
    showLiveAndBroadcast,
    showOrganizations,
    showSchedule,
    snapshot.error,
    snapshot.leagueID,
    snapshot.loading,
    tools,
  ]);
  useAppEnvironmentReport(homeEnvironmentReport);

  if (!snapshot.leagueID) {
    return (
      <div className="home-grid">
        <SBCard>
          <h2>{greeting}</h2>
          <p className="home-hint">Join or create a league workspace to see competitions, athletes, and GPS tools here.</p>
        </SBCard>
      </div>
    );
  }

  return (
    <div className="home-grid">
      <header className="home-header home-section" style={enterStyle(0)}>
        <div className="home-header-copy">
          {currentUser?.activeContext?.displayName && <p className="home-eyebrow">{currentUser.activeContext.displayName}</p>}
          <h1>{greeting}</h1>
        </div>
        {!isInitialLoading && glanceItems.length > 0 && (
          <nav className="home-glance" aria-label="At a glance">
            {glanceItems.map((item) => (
              <Link key={item.id} to={item.to} className="home-glance-item">
                <strong>{item.count}</strong>
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        )}
      </header>

      {snapshot.error && <ErrorText>{snapshot.error}</ErrorText>}
      {isInitialLoading ? (
        <LoadingView label="Loading league…" />
      ) : (
        <>
          {showSchedule && (
            <div className="home-hero home-section" style={enterStyle(1)}>
              <SBCard className="home-card home-next">
                <div className="home-card-head">
                  <h3>Next up</h3>
                  {nextUp ? (
                    <span className="home-next-when">{nextUpWhen}</span>
                  ) : (
                    <Link to="/home/schedule">All ({competitionCount})</Link>
                  )}
                </div>
                {nextUpDisplay ? (
                  <>
                    <div className="home-next-body">
                      <div className="event-date home-next-date" aria-hidden="true">
                        {nextUpStartsAt ? (
                          <>
                            <span className="event-date-month">{nextUpStartsAt.toLocaleString(undefined, { month: "short" })}</span>
                            <span className="event-date-day">{nextUpStartsAt.getDate()}</span>
                          </>
                        ) : (
                          <span className="event-date-tbd">TBD</span>
                        )}
                      </div>
                      <div className="home-next-main">
                        <h2 className="home-next-name">
                          <CompetitionName name={nextUpDisplay.eventName} />
                        </h2>
                        <time className="home-next-meta" dateTime={nextUpStartsAt?.toISOString()}>
                          {nextUpDateText}
                          {nextUpLocation ? ` · ${nextUpLocation}` : ""}
                        </time>
                        <div className="event-row-badges">
                          {nextUpDisplay.isGPSRace && <span className="badge badge-gps">GPS</span>}
                          <span className={`badge badge-${nextUpDisplay.instance.status}`}>{nextUpDisplay.instance.status}</span>
                        </div>
                      </div>
                    </div>
                    <div className="home-pills">
                      <Link to={`/competitions/${nextUpDisplay.instance.id}`} className="home-pill home-pill-primary">
                        Open competition
                      </Link>
                      {nextUpDisplay.isGPSRace && showGPS && (
                        <Link to={`/competitions/${nextUpDisplay.instance.id}/gps/map`} className="home-pill">
                          <Glyph kind="gps" />
                          <span>Live map</span>
                        </Link>
                      )}
                      {nextUpDisplay.isGPSRace && showBroadcastOverlays && (
                        <Link to={`/competitions/${nextUpDisplay.instance.id}/overlays`} className="home-pill">
                          <Glyph kind="overlay" />
                          <span>Overlays</span>
                        </Link>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="home-hint">No upcoming competitions.</p>
                )}
              </SBCard>

              <SBCard className="home-card">
                <div className="home-card-head">
                  <h3>Schedule</h3>
                  <Link to="/home/schedule">All ({competitionCount})</Link>
                </div>
                {schedule.length === 0 ? (
                  <p className="home-hint">{nextUp ? "No other published competitions." : "No published competitions yet."}</p>
                ) : (
                  schedule.map((display) => <EventRow key={display.instance.id} display={display} />)
                )}
              </SBCard>
            </div>
          )}

          {tools.length > 0 && (
            <section className="home-tools home-section" style={enterStyle(2)} aria-label="Tools">
              <div className="home-card-head">
                <h3>Tools</h3>
              </div>
              <div className="home-tools-grid" style={{ "--home-tools-count": tools.length } as CSSProperties}>
                {tools.map((tool) => (
                  <Link key={tool.id} to={tool.to} className="home-tool">
                    <RowIcon kind={tool.icon} />
                    <span className="home-tool-copy">
                      <strong>{tool.label}</strong>
                      <span>{tool.subtitle}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {(showOrganizations || showLiveAndBroadcast) && (
            <div className="home-bottom home-section" style={enterStyle(3)}>
              {showOrganizations && (
                <SBCard className="home-card">
                  <div className="home-card-head">
                    <h3>Organizations</h3>
                    <Link to="/home/organizations">All ({organizationCount})</Link>
                  </div>
                  {organizationCount === 0 ? (
                    <p className="home-hint">No organizations yet.</p>
                  ) : (
                    <div className="home-org-grid">
                      {snapshot.organizations.slice(0, 3).map((org) => (
                        <Link key={org.id} to={`/organizations/${org.id}`} className="home-org">
                          <RowIcon kind="org" />
                          <span className="home-org-copy">
                            <strong title={org.name}>{org.name}</strong>
                            <span>
                              {org.athleteUserIDs.length} athletes · {org.coachUserIDs.length} coaches
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </SBCard>
              )}

              {showLiveAndBroadcast && (
                <SBCard className="home-card">
                  <div className="home-card-head">
                    <h3>Live &amp; broadcast</h3>
                    {gpsRaces.length > 0 && (
                      <span className="home-live-count">
                        {gpsRaces.length} GPS {gpsRaces.length === 1 ? "race" : "races"}
                      </span>
                    )}
                  </div>
                  {gpsRaces.length === 0 ? (
                    <p className="home-hint">
                      {showBroadcastOverlays
                        ? "Mark a competition as a GPS Race to design live overlays and lower thirds for TriCaster/OBS."
                        : "No GPS-enabled competitions."}
                    </p>
                  ) : (
                    gpsRaces.map((display) => (
                      <div key={display.instance.id} className="home-live-row">
                        <span className="home-live-main">
                          <Link to={`/competitions/${display.instance.id}`} title={display.eventName}>
                            {display.eventName}
                          </Link>
                          <span>
                            {display.startsAt
                              ? display.startsAt.toLocaleString(undefined, { month: "short", day: "numeric" })
                              : "Unscheduled"}
                          </span>
                        </span>
                        <span className="home-live-actions">
                          {showGPS && (
                            <Link to={`/competitions/${display.instance.id}/gps/map`} className="home-pill home-pill-small">
                              Map
                            </Link>
                          )}
                          {showBroadcastOverlays && (
                            <Link to={`/competitions/${display.instance.id}/overlays`} className="home-pill home-pill-small">
                              Overlays
                            </Link>
                          )}
                        </span>
                      </div>
                    ))
                  )}
                </SBCard>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
